import "./ManageCustomAppsModal.scss";

import React, { useState } from "react";

import { Alert, Button, message, Modal, Select, Typography } from "antd";
import PropTypes from "prop-types";

import { useAppTheme } from "../../../context/ThemeProvider";
import { showAppError } from "../../../Utils/error";
import { getSingularOrPlural } from "../../../Utils/utilityFunctions";
import FeaturesAPI from "../../api/FeaturesAPI";
import {
  APP_ACTION_OPTIONS,
  APP_ACTIONS,
  CUSTOM_APP_OPTIONS,
} from "../manageFeaturesUtils";
import UploadUsersCSV from "./UploadUsersCSV";
import UsersTable from "./UsersTable";

const ManageCustomAppsModal = ({ open, onClose, onSuccess }) => {
  const { COLORS } = useAppTheme();

  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [csvUploadSuccess, setCsvUploadSuccess] = useState(0);
  const [csvUploadFailed, setCsvUploadFailed] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const resetModal = () => {
    setCsvFile(null);
    setCsvData([]);
    setSelectedApp(null);
    setSelectedAction(null);
    setCsvUploadSuccess(0);
    setCsvUploadFailed(0);
  };

  const handleCancel = () => {
    onClose();
    resetModal();
  };

  const handleCsvUpload = (uploadedFile, data, successCount, failedCount) => {
    setCsvFile(uploadedFile);
    setCsvData(data);
    setCsvUploadSuccess(successCount);
    setCsvUploadFailed(failedCount);
  };

  const handleChangeCsv = () => {
    resetModal();
  };

  const handleSave = async () => {
    if (!selectedApp) {
      message.error("Please select an app to proceed");
      return;
    }

    if (!selectedAction) {
      message.error("Please select an action to proceed");
      return;
    }

    const validUsers = csvData.filter((user) => user.status === "valid");

    if (validUsers.length === 0) {
      message.error("No valid users to process.");
      return;
    }

    setIsSaving(true);

    try {
      const list = validUsers.map((user) => ({
        host: user.host,
        app: selectedApp,
      }));

      let resp;

      if (selectedAction === APP_ACTIONS.INSTALL) {
        resp = await Promise.allSettled(
          list.map((item) => FeaturesAPI.installCustomAppInHost(item))
        );
      } else if (selectedAction === APP_ACTIONS.UNINSTALL) {
        resp = await Promise.allSettled(
          list.map((item) => FeaturesAPI.uninstallCustomAppFromHost(item))
        );
      }

      const actionLabel =
        selectedAction === APP_ACTIONS.INSTALL ? "installed" : "uninstalled";
      const appLabel =
        CUSTOM_APP_OPTIONS.find((app) => app.value === selectedApp)?.label ||
        selectedApp;

      const totalCount = resp.length;
      const failedCount = resp.filter((r) => r.status === "rejected").length;

      if (failedCount > 0) {
        message.error(
          `Failed to ${selectedAction} ${appLabel} for ${failedCount} ${getSingularOrPlural(
            failedCount,
            "user"
          )}`
        );

        // If all users failed, return
        if (totalCount === failedCount) {
          return;
        }
      }

      message.success(
        `Successfully ${actionLabel} ${appLabel} for ${
          validUsers.length
        } ${getSingularOrPlural(validUsers.length, "user")}`
      );
      onSuccess?.(validUsers.length);
      handleCancel();
    } catch (error) {
      showAppError(error);
    } finally {
      setIsSaving(false);
      handleCancel();
    }
  };

  const renderContent = () => {
    if (!csvFile) {
      return (
        <UploadUsersCSV
          onUpload={handleCsvUpload}
          onError={() =>
            message.error("Wrong CSV format. Please check and try again.")
          }
        />
      );
    }

    return (
      <div className="csv-upload-success">
        <div className="csv-success-header">
          <Typography.Title level={4}>
            CSV uploaded successfully
          </Typography.Title>
          <Button
            type="link"
            onClick={handleChangeCsv}
            style={{ color: COLORS.INFO }}
          >
            Change CSV
          </Button>
        </div>

        <div className="upload-stats">
          {csvUploadSuccess > 0 && (
            <Alert
              message={`${csvUploadSuccess} ${getSingularOrPlural(
                csvUploadSuccess,
                "user"
              )} successfully imported`}
              type="success"
              style={{ marginBottom: 8 }}
            />
          )}

          {csvUploadFailed > 0 && (
            <Alert
              message={`${csvUploadFailed} ${getSingularOrPlural(
                csvUploadFailed,
                "user"
              )} failed to import (check table for errors)`}
              type="warning"
              style={{ marginBottom: 8 }}
            />
          )}
        </div>

        <div className="app-selection-section">
          <div className="selection-row">
            <div className="selection-item">
              <Typography.Text strong>Select app</Typography.Text>
              <Select
                size="large"
                placeholder="Choose an app"
                value={selectedApp}
                onChange={setSelectedApp}
                options={CUSTOM_APP_OPTIONS}
                style={{ width: "100%" }}
              />
            </div>

            <div className="selection-item">
              <Typography.Text strong>Select what to do</Typography.Text>
              <Select
                size="large"
                placeholder="Choose action"
                value={selectedAction}
                onChange={setSelectedAction}
                options={APP_ACTION_OPTIONS}
                style={{ width: "100%" }}
              />
            </div>
          </div>
        </div>

        <UsersTable data={csvData} />
      </div>
    );
  };

  return (
    <Modal
      title="Install/Uninstall Custom Apps"
      visible={open}
      open={open}
      onCancel={handleCancel}
      width={1024}
      className="manage-custom-apps-modal"
      centered
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={isSaving}
          disabled={csvData.length === 0 || !selectedApp || !selectedAction}
          onClick={handleSave}
        >
          Save
        </Button>,
      ]}
      destroyOnClose
    >
      <div className="modal-content-wrapper">{renderContent()}</div>
    </Modal>
  );
};

ManageCustomAppsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default ManageCustomAppsModal;
